import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    registerUser,
    fetchUserProfile,
    obtainToken,
    googleLogin as googleLoginRequest,
    googleLink as googleLinkRequest,
} from "../api/auth";

// Helper to set token
const setToken = (token) => {
    localStorage.setItem('access_token', token);
};

// Helper to remove token
const removeToken = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
};

export const useAuth = () => {
    const queryClient = useQueryClient();

    // Query for user profile (enabled only if token exists)
    const { data: user, isLoading: isLoadingUser, isError } = useQuery({
        queryKey: ['user'],
        queryFn: fetchUserProfile,
        enabled: !!localStorage.getItem('access_token'),
        retry: false,
    });

    // If fetching user fails (e.g. invalid token), logout
    useEffect(() => {
        if (isError) {
            removeToken();
            queryClient.setQueryData(['user'], null);
        }
    }, [isError, queryClient]);

    // Shared by every path that signs someone in. Whatever issued the pair --
    // /token/, /google/ or /google/link/ -- the tab is only actually logged in
    // once these land in localStorage and the user query is refetched.
    const storeSession = (data) => {
        if (!data?.access) return;
        setToken(data.access);
        if (data.refresh) {
            localStorage.setItem('refresh_token', data.refresh);
        }
        queryClient.invalidateQueries({ queryKey: ['user'] });
    };

    // Login Mutation (Using Token Endpoint for JWT)
    const loginMutation = useMutation({
        mutationFn: async ({ email, password }) => {
            // Adjust based on your backend: 
            // If /log-in uses session, use loginUser.
            // If you want JWT, use obtainToken. 
            // Based on checking users/urls.py, both exist. 
            // Let's assume we want JWT for React SPA.
            const data = await obtainToken({ email, password });
            return data;
        },
        onSuccess: (data) => storeSession(data),
    });

    // Google sign-in ends at the same place password sign-in does: a token
    // pair that has to land in localStorage before anything else can read it.
    const googleLoginMutation = useMutation({
        mutationFn: googleLoginRequest,
        onSuccess: (data) => storeSession(data),
    });

    // Step two of the unverified-account conflict. It also ends in a token
    // pair, so it signs the tab in exactly like the two above.
    const googleLinkMutation = useMutation({
        mutationFn: googleLinkRequest,
        onSuccess: (data) => storeSession(data),
    });

    // Signup Mutation
    const signupMutation = useMutation({
        mutationFn: registerUser,
        onSuccess: () => {
            // Auto login or redirect could be added here
            alert("Account created! Please log in.");
        },
    });

    const logout = () => {
        removeToken();
        queryClient.setQueryData(['user'], null);
        queryClient.invalidateQueries({ queryKey: ['user'] });
    };

    return {
        user,
        isLoadingUser,
        login: loginMutation.mutateAsync,
        isLoggingIn: loginMutation.isPending,
        signup: signupMutation.mutateAsync,
        isSigningUp: signupMutation.isPending,
        googleLogin: googleLoginMutation.mutateAsync,
        isGoogleLoggingIn: googleLoginMutation.isPending,
        googleLink: googleLinkMutation.mutateAsync,
        isGoogleLinking: googleLinkMutation.isPending,
        logout,
        isAuthenticated: !!user,
    };
};
